import React, { createContext, useContext, useState } from "react";
import { useLoaderData } from "react-router-dom";
import HospitalApi from "../api/hospital";

// =========================
// CACHE
// =========================

const getCachedHospital = () => {
  try {
    const raw = localStorage.getItem("hospital");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedHospital = (hospital) => {
  localStorage.setItem("hospital", JSON.stringify(hospital));
};


// =========================
// CONTEXT
// =========================

export const HospitalContext = createContext(null);


// =========================
// HOOKS
// =========================

export const useHospital = () => {
  const ctx = useContext(HospitalContext);
  if (!ctx) {
    throw new Error("useHospital must be used within HospitalProvider");
  }
  return ctx;
};

export const useOptionalHospital = () => {
  return useContext(HospitalContext);
};


// =========================
// LOADER (React Router)
// =========================

export async function hospitalLoader({ params }) {
  const cached = getCachedHospital();
  if (cached) return cached;

  const { hospitalName } = params;

  // No hospital loaded!
  if (!hospitalName) {
    return null;
  }

  const hospitals = await HospitalApi.getHospitalList();

  const match = hospitals.find(
    h => h.name.toLowerCase() === hospitalName.toLowerCase()
  );

  if (!match) {
    throw new Response("Hospital not found", { status: 404 });
  }

  const hospital = await HospitalApi.getHospital(match.id);

  if (!hospital) {
    throw new Response("Hospital not found", { status: 404 });
  }

  setCachedHospital(hospital);
  return hospital;
}


// =========================
// PROVIDER
// =========================

const HospitalProvider = ({ children }) => {
  const [hospital] = useState(() => getCachedHospital());

  return (
    <HospitalContext.Provider value={{ hospital }}>
      {children}
    </HospitalContext.Provider>
  );
};

export function HospitalLayout({children}) {
  const hospital = useLoaderData();

  return (
    <HospitalProvider initialHospitalData={hospital}>
      {children}
    </HospitalProvider>
  );
}

export default HospitalProvider;